/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Outlet } from 'react-router-dom';
import { type FC, useEffect } from 'react';

import { useUpdate } from 'ahooks';
import { useSystemConfig } from '@adp-foundation/global-store';
import { BrowserUpgradeWrap } from '@adp-foundation/browser-upgrade-banner';
import { I18nProvider } from '@adp-arch/i18n/i18n-provider';
import { I18n, resolveLocale, toI18nLanguage } from '@adp-arch/i18n';
import { useUserInfo } from '@adp-arch/foundation-sdk';
import { ThemeProvider } from '@coze-arch/coze-design';
import { LocaleProvider, zhCN, enUS, zhTW } from '@adp-arch/bot-semi';

// Helper to get Semi UI locale config
const getSemiLocale = (locale: string) => {
  switch (locale) {
    case 'en-US':
      return enUS;
    case 'zh-HK':
      // zh-HK (Hong Kong Traditional Chinese) - use zhTW from Semi UI as closest match
      // If zhTW is undefined, fallback to zhCN
      return zhTW || zhCN;
    case 'zh-CN':
    default:
      return zhCN;
  }
};

import { GlobalLayoutComposed } from '@/components/global-layout-composed';

import { useBrandFavicon } from './use-brand-favicon';

export const GlobalLayout: FC = () => {
  const userInfo = useUserInfo();
  const update = useUpdate();
  const { brandConfiguration, defaultLocale } = useSystemConfig();
  useBrandFavicon(brandConfiguration?.favicon_url);
  const currentLocale = resolveLocale(userInfo?.locale, defaultLocale);

  // For historical reasons, en-US needs to be converted to en.
  // zh-HK should remain as zh-HK for proper i18n resource loading
  const transformedCurrentLocale = toI18nLanguage(currentLocale);

  useEffect(() => {
    document.documentElement.lang = currentLocale;
    if (I18n.language !== transformedCurrentLocale) {
      I18n.setLang(transformedCurrentLocale);
      // Force an update, otherwise the language switch will not take effect
      update();
    }
  }, [currentLocale, transformedCurrentLocale, update]);

  const semiLocale = getSemiLocale(currentLocale);

  // Debug: log locale information
  if (process.env.NODE_ENV === 'development') {
    console.log(
      '[GlobalLayout] currentLocale:',
      currentLocale,
      'semiLocale:',
      semiLocale,
    );
  }

  return (
    <I18nProvider i18n={I18n}>
      <LocaleProvider locale={semiLocale || enUS}>
        <ThemeProvider
          defaultTheme="light"
          changeSemiTheme={true}
          changeBySystem={IS_BOE}
        >
          <BrowserUpgradeWrap>
            <GlobalLayoutComposed>
              <GlobalTitleUpdater />
              <Outlet />
            </GlobalLayoutComposed>
          </BrowserUpgradeWrap>
        </ThemeProvider>
      </LocaleProvider>
    </I18nProvider>
  );
};

const GlobalTitleUpdater: FC = () => {
  const { brandConfiguration } = useSystemConfig();
  const productName = brandConfiguration?.product_name?.trim() || 'ADP';

  useEffect(() => {
    try {
      const originalDescriptor = Object.getOwnPropertyDescriptor(
        Document.prototype,
        'title',
      );

      Object.defineProperty(document, 'title', {
        get() {
          return productName;
        },
        set() {
          // No-op to ignore external writes
        },
        configurable: true,
      });

      const titleEl = document.querySelector('title');
      if (titleEl) {
        titleEl.textContent = productName;

        const observer = new MutationObserver(() => {
          if (titleEl.textContent !== productName) {
            titleEl.textContent = productName;
          }
        });

        observer.observe(titleEl, { childList: true });

        return () => {
          observer.disconnect();
          if (originalDescriptor) {
            Object.defineProperty(document, 'title', originalDescriptor);
          }
        };
      }
    } catch (e) {
      console.error('Failed to override document.title:', e);
    }
  }, [productName]);

  return null;
};
